import { useState } from "react";
import JuicyButton from "@/components/base/JuicyButton";

const commands = [
  {
    name: "/timetable today",
    desc: "今日の時間割を表示する",
    usage: "/timetable today",
    example: "📅 今日（月曜日）の時間割を表示します",
  },
  {
    name: "/timetable week",
    desc: "今週の時間割を一覧表示する",
    usage: "/timetable week",
    example: "📋 月曜〜金曜の全授業を一覧で表示します",
  },
  {
    name: "/timetable day",
    desc: "指定した曜日の時間割を表示する",
    usage: "/timetable day:[曜日]",
    example: "例: /timetable day:水曜",
  },
  {
    name: "/timetable period",
    desc: "曜日と時限を指定して授業を確認する",
    usage: "/timetable period:[曜日] [時限]",
    example: "例: /timetable period:月曜 3",
  },
  {
    name: "/timetable next",
    desc: "次の授業を表示する",
    usage: "/timetable next",
    example: "⏭ 次の時限の授業を表示します",
  },
  {
    name: "/help",
    desc: "使い方とコマンド一覧を表示する",
    usage: "/help",
    example: "📖 時間割くんの使い方を表示します",
  },
];

export default function CommandsSection() {
  const [selected, setSelected] = useState(commands[0].name);
  const active = commands.find((c) => c.name === selected) ?? commands[0];

  return (
    <section id="commands" className="py-32 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-gray-400 mb-4">コマンド</p>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-black leading-tight">
            スラッシュコマンドで<br />すぐに使える。
          </h2>
          <p className="text-gray-500 text-sm max-w-xs leading-relaxed">
            「/」を入力するだけで候補が表示されます。むずかしい操作は一切不要です。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Command list */}
          <div className="flex flex-col gap-2">
            {commands.map((cmd) => (
              <JuicyButton
                key={cmd.name}
                onClick={() => setSelected(cmd.name)}
                particleColor={selected === cmd.name ? "#ffffff" : "#000000"}
                className={`text-left px-5 py-4 rounded-2xl transition-colors duration-200 cursor-pointer w-full ${
                  selected === cmd.name
                    ? "bg-black text-white"
                    : "bg-gray-50 text-black hover:bg-gray-100"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono font-semibold text-sm">{cmd.name}</span>
                  <i className={`ri-arrow-right-line text-sm transition-opacity ${selected === cmd.name ? "opacity-100" : "opacity-0"}`}></i>
                </div>
                <p className={`text-xs mt-1 ${selected === cmd.name ? "text-white/60" : "text-gray-400"}`}>
                  {cmd.desc}
                </p>
              </JuicyButton>
            ))}
          </div>

          {/* Preview panel */}
          <div className="sticky top-24">
            <div className="bg-gray-950 rounded-3xl overflow-hidden">
              {/* Terminal bar */}
              <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/5">
                <span className="w-3 h-3 rounded-full bg-white/10"></span>
                <span className="w-3 h-3 rounded-full bg-white/10"></span>
                <span className="w-3 h-3 rounded-full bg-white/10"></span>
                <span className="ml-3 text-xs text-white/30 font-mono"># 時間割チャンネル</span>
              </div>

              {/* Chat area */}
              <div className="p-6 min-h-52 flex flex-col gap-4">
                {/* User message */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-white/50 text-xs"></i>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs mb-1">あなた</p>
                    <div className="bg-white/5 rounded-xl px-4 py-2.5">
                      <code className="text-white font-mono text-sm">{active.usage}</code>
                    </div>
                  </div>
                </div>

                {/* Bot response */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src="https://storage.readdy-site.link/project_files/caeb4770-bff8-4a6c-aacf-b2169351eb6e/f9f885c4-cf1a-4153-aa6b-79a8a47d3db7_unnamed.png?v=32f9e45271c38a3f927699f78a81a4f1"
                      alt="時間割くん"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs mb-1">時間割くん <span className="bg-white/10 text-white/40 text-[10px] px-1.5 py-0.5 rounded ml-1">BOT</span></p>
                    <div className="bg-white/5 rounded-xl px-4 py-3">
                      <p className="text-white/80 text-sm leading-relaxed">{active.example}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage hint */}
            <div className="mt-4 px-2">
              <p className="text-gray-400 text-xs">
                <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-600">{active.usage}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
